import type { EdgeBar } from '../types/chart'
import { alignTimestamp, type Period, type RegularSessionBuckets } from '../utils/resolution'
import { mergeSubBar, nextSubRung, subPeriodFor } from '../utils/replaySubBars'
import { pageMsFromBars } from './DataLayer'
import type { EventBus } from './EventBus'

// ─── Debug logger (toggle from console: window.__chartDebug = true) ───
const _win = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>) : null
function replayDebug(...args: unknown[]) {
  if (_win?.__chartDebug) console.log('[Replay]', ...args)
}
const iso = (ts: number | null | undefined) => (ts == null ? 'null' : new Date(ts).toISOString())

/**
 * Public replay state. `forwardBuffer`/`exhaustion` internals are deliberately
 * NOT surfaced — `getState()` returns this shape only. Phase 2's control strip
 * and EdgeChart's `replayActive` flag build against exactly these fields.
 */
export interface ReplayState {
  active: boolean
  playing: boolean
  cursorTs: number | null
  speed: number // bars per second (0.5–10)
  exhausted: boolean // stepped up to live edge
  /** True once at least one bar was applied this session — exhausted with
   *  `stepped: false` means "no data ahead of the start", not "caught up". */
  stepped: boolean
  /** True while a highlighted range is replaying on repeat (see `startLoop`). */
  looping: boolean
}

/**
 * Narrow dependency seam so the controller unit-tests without a real chart.
 * ChartManager wires these to real DataLayer/chart calls in `getReplay()`.
 */
export interface ReplayDeps {
  /**
   * Fetch bars for the active symbol at `period` in `[from, to]`, using the
   * same session options (`market_session`, `adjusted`) as the initial load —
   * the DataLayer owns that so forward stepping can't leak AH bars into an RTH
   * chart. `period` leads the signature because sub-bar replay fetches a FINER
   * resolution than the displayed period (spec Addendum §"Sub-bar playback").
   * Returned bars need not be sorted; the controller sorts + dedups.
   */
  fetchBars(period: Period, from: number, to: number): Promise<EdgeBar[]>
  /**
   * Set (ts) or clear (null) the DataLayer focus anchor. `ts` runs the
   * "enter replay" dance (anchor + live unsubscribe + bounded reload); `null`
   * restores live (resubscribe + reload to now). Maps to
   * ChartManager.applyFocusTimestamp. May return the reload promise —
   * `enter()` awaits it so the forward prefetch can't race the anchored
   * reload (a fill mid-reload can observe stale DataLayer state).
   */
  applyAnchor(ts: number | null): void | Promise<void>
  /** Apply one bar incrementally — same `chart.updateData` path a live tick uses. */
  appendBar(bar: EdgeBar): void
  /** Active period, read live so a mid-replay timeframe switch is picked up. */
  periodOf(): Period
  /**
   * The chart's regular-session bucketing (null: clock buckets), read live
   * like `periodOf`. With it, 1H/4H candles start at the 09:30 open as the
   * chart's history bars do.
   */
  regularSession?(): RegularSessionBuckets | null
  /**
   * The chart's CURRENT focus anchor (DataLayer.getAnchorTimestamp), null when
   * the chart is live. Read once at `enter()` so `exit()` can put the user back
   * where they were: a replay launched from a historical day returns to that
   * day, not to today (spec 2026-08-27 §"Exit restores the anchor you came from").
   */
  currentAnchor(): number | null
  /** Clock seam (faked in tests). */
  nowMs(): number
  eventBus: EventBus
}

const MIN_SPEED = 0.5
const MAX_SPEED = 10
const DEFAULT_SPEED = 1
/** Playback floor: never schedule steps faster than this, whatever the speed. */
const MIN_STEP_MS = 50
/** Refill the forward buffer once it drops below this many bars. */
const LOW_WATER = 8
/**
 * Bounded forward walk per fill — mirrors DataLayer's backward sparse-probe
 * (`DataLayer.ts:931`). Each probe advances one page across an empty
 * weekend/holiday window; the cap keeps a persistent gap from looping.
 */
const MAX_PROBES = 4
/**
 * Hard cap on fast-forward sub-bar applies during a timeframe switch. A single
 * partial bucket holds at most ~13 sub-bars (1D/30m) up to ~78 (1W/1D), so
 * this only ever trips on a pathological gap — it's a loop backstop, not a
 * functional limit.
 */
const FAST_FORWARD_CAP = 5000

/**
 * Bar replay engine. Owned by ChartManager (like DataLayer).
 * Enter anchors the chart at a past instant (future bars withheld, live feed
 * off); step/play walk forward by feeding SUB-resolution bars (spec Addendum
 * §"Sub-bar playback") that accrete into the forming display candle via the
 * incremental `updateData` path — so a higher-timeframe bar FORMS during
 * replay instead of popping in whole, and indicators + OHLC readouts track for
 * free. Ephemeral — nothing is persisted.
 *
 * The forward buffer holds sub-bars at `subPeriodFor(displayPeriod)`; scan
 * windows, probes, and the live-edge test are all in sub-period units. The
 * cursor advances at sub-bar granularity (the replay clock ticks minute-by-
 * minute while a 5m candle forms), while the chart shows the aggregating
 * display bucket.
 *
 * Spec: docs/superpowers/specs/2026-07-07-bar-replay-design.md
 */
export class ReplayController {
  private readonly deps: ReplayDeps
  private state: ReplayState = {
    active: false,
    playing: false,
    cursorTs: null,
    speed: DEFAULT_SPEED,
    exhausted: false,
    stepped: false,
    looping: false,
  }
  /**
   * Escalated sub-resolution, when the ladder rung had NO data for the range
   * (finer series have shorter retention). null = use the ladder. Reset on
   * enter/exit and on a timeframe switch (fresh display → fresh ladder).
   */
  private subEscalation: Period | null = null
  /** Whether any sub-bar was staged at the CURRENT rung — gates escalation. */
  private stagedAnyAtRung = false
  /** Focus anchor the chart carried before `enter()`, restored by `exit()`. */
  private preReplayAnchor: number | null = null
  /** Withheld future SUB-bars staged for stepping, ascending by timestamp. */
  private forwardBuffer: EdgeBar[] = []
  /** Timestamp of the last sub-bar staged into `forwardBuffer` (fill high-water). */
  private lastBufferedTs: number | null = null
  /** True once the forward walk reaches `now` — no more history to withhold. */
  private reachedLiveEdge = false
  /** In-flight fill promise; shared by concurrent callers (no double-fetch). */
  private fillPromise: Promise<void> | null = null
  /**
   * DISPLAY period (ms) the buffer was last filled for — detects a timeframe
   * switch. Tracks the display period (not the sub period) so a re-anchor's own
   * `data:loaded` short-circuits `onDataLoaded` on the same display timeframe.
   */
  private lastFilledPeriodMs: number | null = null
  /** The instant replay was entered at (the first sub-bar to reveal). */
  private startTs: number | null = null
  /** Timestamp of the last SUB-bar actually applied to the chart. */
  private lastAppliedTs: number | null = null
  /**
   * The forming display candle — the running aggregate of the current bucket's
   * sub-bars. `appendBar(forming)` re-applies it on every sub-step: a same-ts
   * update while the bucket is open, a fresh append when a new bucket starts.
   */
  private forming: EdgeBar | null = null
  /** Display-bucket boundary the `forming` candle belongs to (null = none yet). */
  private formingBucketTs: number | null = null
  /**
   * Exclusive lower bound for admitting fetched sub-bars into the buffer (a bar
   * passes when `timestamp > fillFloorTs`). Set below the first sub-bar to
   * reveal on enter (`startTs − 1`) and re-based to the partial bucket's start
   * on a timeframe switch so that bucket can re-form from its sub-bars.
   */
  private fillFloorTs = Number.NEGATIVE_INFINITY
  /**
   * Next forward-fetch window start. Persists across fills so repeated
   * probes ADVANCE through a wide data gap (e.g. a date beyond intraday
   * retention) instead of rescanning the same empty pages forever.
   */
  private scanFromTs: number | null = null
  /** Last anchor handed to applyAnchor — dedups the TF-switch re-anchor. */
  private lastAnchorTs: number | null = null
  /** Loop-range bounds (normalized start < end), null when not looping. The
   *  end is functional but never drawn - see replayLoopOverlay. */
  private loopStartTs: number | null = null
  private loopEndTs: number | null = null
  /**
   * Sub-bars applied since the last loop wrap. A wrap that arrives with ZERO
   * progress means the range can never play (no data inside it, or an end
   * placed in future whitespace) - the loop stops instead of spinning an
   * infinite reload cycle.
   */
  private loopProgress = 0
  /** Consecutive wraps whose pass applied <2 sub-bars - see the circuit
   *  breaker in `wrapLoop`. Survives across restarts by design. */
  private thinWraps = 0
  /**
   * True while a loop wrap's re-anchor + refill is in flight. `step()` is
   * inert during it, mirroring how `enter()` awaits `applyAnchor` before the
   * first fill: a fill racing the anchored reload can observe stale DataLayer
   * state. `playing` stays true across a wrap so the strip doesn't flicker.
   */
  private restarting = false
  /**
   * Bumped on every buffer reset (wrap restart, exit). A `doFill` that awoke
   * from a fetch under a stale epoch discards its result instead of admitting
   * bars gathered for a scan position that no longer exists.
   */
  private fillEpoch = 0
  private timer: ReturnType<typeof setTimeout> | null = null
  private readonly offDataLoaded: () => void

  constructor(deps: ReplayDeps) {
    this.deps = deps
    // A mid-replay timeframe switch re-anchors ≤ cursorTs on the new period and
    // emits `data:loaded`; rebuild the forward buffer for the new period then.
    this.offDataLoaded = deps.eventBus.on('data:loaded', this.onDataLoaded)
  }

  getState(): ReplayState {
    return { ...this.state }
  }

  /**
   * Enter replay at `startTs`: the chart keeps everything strictly BEFORE the
   * start BUCKET (anchor = `alignTimestamp(startTs, displayPeriod) − 1`) and the
   * start bucket then forms sub-bar by sub-bar from `startTs` onward.
   *
   * Bucket alignment (not the raw `startTs − 1`) matters on coarse timeframes:
   * a 1D bar is stamped at day-start, so `startTs − 1` (mid-day) would wrongly
   * INCLUDE day D's already-complete daily bar. Aligning to the bucket excludes
   * it, so day D forms from scratch (spec Addendum §"Anchor correction"). On an
   * intraday TF whose bucket start equals `startTs` this is unchanged.
   */
  async enter(startTs: number): Promise<void> {
    if (this.state.active) return
    // Snapshot BEFORE anchoring: once applyAnchor runs, the DataLayer reports
    // the replay anchor and the day the user came from is unrecoverable.
    this.preReplayAnchor = this.deps.currentAnchor()
    this.state.active = true
    this.state.playing = false
    this.state.exhausted = false
    this.state.stepped = false
    this.state.cursorTs = startTs
    this.startTs = startTs
    this.subEscalation = null
    this.stagedAnyAtRung = false
    this.lastAppliedTs = null
    this.scanFromTs = startTs
    this.fillFloorTs = startTs - 1
    const anchor = alignTimestamp(startTs, this.deps.periodOf(), this.deps.regularSession?.()) - 1
    this.lastAnchorTs = anchor
    this.forwardBuffer = []
    this.lastBufferedTs = null
    this.reachedLiveEdge = false
    this.lastFilledPeriodMs = null
    this.forming = null
    this.formingBucketTs = null
    replayDebug('enter', { startTs: iso(startTs), anchor: iso(anchor) })
    // Emit BEFORE awaiting the reload so the control strip appears instantly.
    this.deps.eventBus.emit('replay:started', { cursorTs: startTs })
    try {
      await this.deps.applyAnchor(anchor)
    } catch {
      // Anchor reload failure isn't fatal to the session — stepping will
      // retry the fill; the user can also exit cleanly.
    }
    await this.fillForward().catch(() => undefined)
  }

  /**
   * Leave replay and put the chart back where the user was: the focus anchor
   * captured at `enter()`. That is `null` (live, resubscribe + reload to now)
   * for a replay launched from a live chart, and the original historical
   * instant for one launched from a pinned day.
   *
   * `restoreTo` overrides that capture. ChartManager passes `null` when the
   * host EXPLICITLY asked to go live (`setFocusTimestamp(null)`, a workspace
   * date-broadcast reset, say): an explicit request beats the day the replay
   * happened to start from.
   */
  exit(opts?: { restoreTo?: number | null }): void {
    if (!this.state.active) return
    this.stopTimer()
    this.fillEpoch += 1
    this.state.active = false
    this.state.playing = false
    this.state.exhausted = false
    this.state.stepped = false
    this.state.looping = false
    this.loopStartTs = null
    this.loopEndTs = null
    this.loopProgress = 0
    this.restarting = false
    this.state.cursorTs = null
    this.subEscalation = null
    this.stagedAnyAtRung = false
    this.forwardBuffer = []
    this.lastBufferedTs = null
    this.reachedLiveEdge = false
    this.lastFilledPeriodMs = null
    this.startTs = null
    this.lastAppliedTs = null
    this.scanFromTs = null
    this.lastAnchorTs = null
    this.forming = null
    this.formingBucketTs = null
    this.fillFloorTs = Number.NEGATIVE_INFINITY
    const restoreAnchor =
      opts && 'restoreTo' in opts ? (opts.restoreTo ?? null) : this.preReplayAnchor
    this.preReplayAnchor = null
    // `active` is already false so a re-entrant setFocusTimestamp(null) guard
    // (ChartManager) runs the real reload instead of recursing back here.
    this.deps.applyAnchor(restoreAnchor)
    this.deps.eventBus.emit('replay:ended', undefined)
  }

  /** Apply the next withheld bar. Resolves false when caught up to live.
   *  Never rejects: a failed fill reads as "no bar this tick" — the play loop
   *  (and a re-clicked step) retries rather than dying on a transient error. */
  async step(): Promise<boolean> {
    if (!this.state.active || this.restarting) return false
    // Walk the scan as far as needed to stage the next bar: a wide data gap
    // (dates beyond intraday retention, long halts) is crossed inside ONE
    // step — not one probe budget per play tick, which
    // reads as "play does nothing" for seconds. Bounded by scan progress:
    // a fill that can't advance (persistent fetch failure) ends the walk.
    while (this.forwardBuffer.length === 0 && !this.reachedLiveEdge) {
      const scanBefore = this.scanFromTs
      await this.fillForward().catch(() => undefined)
      if (!this.state.active) return false
      if (this.forwardBuffer.length === 0 && this.scanFromTs === scanBefore) break
    }
    // A loop wrap may have begun while the fill above was awaited (this step
    // passed the entry guard before `restarting` flipped). Applying a bar now
    // would race the wrap's anchored reload - bail; the wrap reschedules.
    if (this.restarting) return false
    const sub = this.forwardBuffer.shift()
    if (!sub) {
      if (this.reachedLiveEdge) {
        // Looping with the range end at/past the last bar: the live edge IS
        // the end of the pass - wrap back instead of declaring exhaustion.
        if (this.state.looping) {
          replayDebug('wrap trigger: live edge, progress', this.loopProgress)
          void this.wrapLoop()
          return false
        }
        // Nothing staged and the walk hit the live edge → exhaustion (once).
        if (!this.state.exhausted) {
          this.state.exhausted = true
          this.deps.eventBus.emit('replay:exhausted', undefined)
          this.pause() // auto-pause: no more bars to play
        }
      }
      return false
    }
    this.applyOneSubBar(sub)
    // Crossed the range's (invisible) end → wrap back to its start. The bound
    // is the END of the end-click's display bucket, so the last candle of the
    // range plays out fully instead of being cut at its first sub-bar.
    const wrapBound = this.loopWrapBoundTs()
    if (this.state.looping && wrapBound != null && sub.timestamp >= wrapBound) {
      replayDebug('wrap trigger: crossed end', iso(sub.timestamp), '>=', iso(wrapBound))
      void this.wrapLoop()
      return true
    }
    // Low-water refill runs async so stepping stays responsive; the in-flight
    // guard collapses this with any concurrent fill.
    if (this.forwardBuffer.length < LOW_WATER && !this.reachedLiveEdge) {
      void this.fillForward()
    }
    return true
  }

  /**
   * Merge one sub-bar into the forming display candle and re-apply it. A new
   * `bucketTs` starts a fresh candle (fresh `appendBar` append); the same
   * `bucketTs` extends the open candle in place via the `updateData` same-ts
   * path. The cursor advances to the SUB-bar's timestamp so the replay clock
   * ticks at sub-bar granularity. Shared by `step()` and the timeframe-switch
   * fast-forward.
   */
  private applyOneSubBar(sub: EdgeBar): void {
    const bucketTs = alignTimestamp(
      sub.timestamp,
      this.deps.periodOf(),
      this.deps.regularSession?.(),
    )
    if (this.formingBucketTs !== bucketTs) {
      // Bucket rolled over — the previous candle is complete; open a new one.
      this.forming = null
      this.formingBucketTs = bucketTs
    }
    this.forming = mergeSubBar(this.forming, sub, bucketTs)
    this.deps.appendBar(this.forming)
    this.state.cursorTs = sub.timestamp
    this.lastAppliedTs = sub.timestamp
    this.state.stepped = true
    this.loopProgress += 1
    this.deps.eventBus.emit('replay:step', { cursorTs: sub.timestamp })
  }

  play(): void {
    if (!this.state.active || this.state.playing || this.state.exhausted) return
    this.state.playing = true
    this.deps.eventBus.emit('replay:played', undefined)
    this.scheduleNext()
  }

  pause(): void {
    this.stopTimer()
    if (!this.state.playing) return
    this.state.playing = false
    this.deps.eventBus.emit('replay:paused', undefined)
  }

  setSpeed(barsPerSec: number): void {
    const clamped = Math.min(MAX_SPEED, Math.max(MIN_SPEED, barsPerSec))
    this.state.speed = clamped
    // If mid-playback, re-arm at the new cadence so the change is immediate.
    if (this.state.playing) {
      this.stopTimer()
      this.scheduleNext()
    }
    // Announce unconditionally: while PAUSED no other replay:* event follows,
    // so without this the control strip's controlled <select> stays pinned to
    // the old value until playback starts.
    this.deps.eventBus.emit('replay:speed', { speed: clamped })
  }

  /** Jump to an arbitrary instant — Phase 3 (step-back / seek). */
  async seek(_ts: number): Promise<void> {
    throw new Error('ReplayController.seek: not implemented (Phase 3)')
  }

  /**
   * Replay the range `[aTs, bTs]` on repeat: rewind to its start (the same
   * anchor dance as `enter`, without leaving replay), play through the end
   * candle, wrap, again - until `stopLoop`/`exit`. Point order doesn't matter
   * (either edge may be clicked first). An end at/past the live edge simply
   * degrades to wrapping there.
   *
   * A range with no interior, or one starting past `now`, can never make
   * progress; it is refused up front (with the ended event so an armed UI
   * resets) rather than arming an infinite reload cycle. The same valve fires
   * at wrap time via `loopProgress` when the range turns out to hold no data.
   */
  async startLoop(aTs: number, bTs: number): Promise<void> {
    if (!this.state.active || this.restarting) return
    const start = Math.min(aTs, bTs)
    const end = Math.max(aTs, bTs)
    if (end <= start || start > this.deps.nowMs()) {
      this.deps.eventBus.emit('replay:loop-ended', undefined)
      return
    }
    // A new range supersedes any running loop wholesale.
    if (this.state.looping) this.stopLoop()
    this.thinWraps = 0
    this.loopStartTs = start
    this.loopEndTs = end
    this.state.looping = true
    replayDebug('startLoop', { start: iso(start), end: iso(end) })
    this.deps.eventBus.emit('replay:loop-started', { startTs: start, endTs: end })
    await this.restartAt(start)
    this.play()
  }

  /**
   * Stop looping: clear the bounds and let playback continue past the old
   * range end as ordinary replay. Deliberately does NOT pause or rewind -
   * least surprise for "turn the loop off".
   */
  stopLoop(): void {
    if (!this.state.looping) return
    this.loopStartTs = null
    this.loopEndTs = null
    this.loopProgress = 0
    this.thinWraps = 0
    this.state.looping = false
    this.deps.eventBus.emit('replay:loop-ended', undefined)
  }

  /**
   * The instant at which a pass wraps: the end of the display bucket the end
   * click landed in (end-INCLUSIVE - the clicked candle plays out). Recomputed
   * per check because a mid-loop timeframe switch changes the bucket size.
   */
  private loopWrapBoundTs(): number | null {
    if (this.loopEndTs == null) return null
    const period = this.deps.periodOf()
    return alignTimestamp(this.loopEndTs, period, this.deps.regularSession?.()) + period.ms
  }

  /** Wrap the loop back to its start, or kill it if passes stop making
   *  progress (see `loopProgress` / `thinWraps`). */
  private async wrapLoop(): Promise<void> {
    if (!this.state.looping || this.loopStartTs == null || this.restarting) return
    // Circuit breaker: a pass that applied nothing can never loop (a range
    // over a data gap, an end in future whitespace); several near-empty
    // passes in a row mean the range is degenerate and each "pass" is just a
    // reload cycle. Stop cleanly instead of spinning reload after reload.
    if (this.loopProgress < 2) {
      this.thinWraps += 1
      if (this.loopProgress === 0 || this.thinWraps >= 5) {
        replayDebug('degenerate loop wrap (progress', this.loopProgress, ') - stopping loop')
        this.stopLoop()
        this.pause()
        return
      }
    } else {
      this.thinWraps = 0
    }
    await this.restartAt(this.loopStartTs)
  }

  /**
   * Re-run `enter()`'s reset-and-anchor dance at `startTs` WITHOUT leaving
   * replay: buffers cleared, chart re-anchored one tick before the start
   * bucket, forward buffer refilled. `playing` is untouched - the strip shows
   * a continuous playback across the wrap; `scheduleNext` re-arms at the end
   * if a play loop was running.
   */
  private async restartAt(startTs: number): Promise<void> {
    this.restarting = true
    this.fillEpoch += 1
    this.stopTimer()
    this.state.exhausted = false
    this.state.cursorTs = startTs
    this.startTs = startTs
    this.subEscalation = null
    this.stagedAnyAtRung = false
    this.lastAppliedTs = null
    this.loopProgress = 0
    this.scanFromTs = startTs
    this.fillFloorTs = startTs - 1
    const anchor = alignTimestamp(startTs, this.deps.periodOf(), this.deps.regularSession?.()) - 1
    this.lastAnchorTs = anchor
    this.forwardBuffer = []
    this.lastBufferedTs = null
    this.reachedLiveEdge = false
    // The display period does NOT change across a wrap, so assert it now:
    // nulling it here (as enter() does) would let the wrap's own reload take
    // the timeframe-switch path in onDataLoaded and reset the buffers a
    // second time, concurrently. A real mid-restart TF switch still lands -
    // onDataLoaded compares against the live periodOf().
    this.lastFilledPeriodMs = this.deps.periodOf().ms
    this.forming = null
    this.formingBucketTs = null
    replayDebug('restartAt', { startTs: iso(startTs), anchor: iso(anchor) })
    // Resync the strip (clock jumps back to the loop start immediately).
    this.deps.eventBus.emit('replay:step', { cursorTs: startTs })
    // Let a stale in-flight fill drain first - its epoch check discards the
    // result, and waiting keeps `fillForward` below from adopting it.
    if (this.fillPromise) await this.fillPromise.catch(() => undefined)
    try {
      await this.deps.applyAnchor(anchor)
    } catch {
      // Non-fatal: stepping retries the fill, and exit stays clean.
    }
    await this.fillForward().catch(() => undefined)
    this.restarting = false
    if (this.state.active && this.state.playing) this.scheduleNext()
  }

  destroy(): void {
    this.stopTimer()
    this.offDataLoaded()
    this.fillEpoch += 1
    this.state.active = false
    this.state.playing = false
    this.state.looping = false
    this.loopStartTs = null
    this.loopEndTs = null
    this.restarting = false
    this.forwardBuffer = []
    this.fillPromise = null
  }

  private stepIntervalMs(): number {
    return Math.max(MIN_STEP_MS, Math.round(1000 / this.state.speed))
  }

  private scheduleNext(): void {
    this.stopTimer()
    this.timer = setTimeout(() => {
      this.timer = null
      if (!this.state.playing) return
      // Re-arm whenever still playing — an empty/failed step is a skipped
      // tick, not a stop. Exhaustion is the only auto-stop, and `step` pauses
      // (playing=false) there itself. Anything else must keep the loop alive
      // or the play button dies silently (playing stuck true, timer gone).
      void this.step().finally(() => {
        if (this.state.playing) this.scheduleNext()
      })
    }, this.stepIntervalMs())
  }

  private stopTimer(): void {
    if (this.timer != null) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  private onDataLoaded = (): void => {
    if (!this.state.active) return
    const displayPeriod = this.deps.periodOf()
    replayDebug(
      'data:loaded seen, samePeriod',
      displayPeriod.ms === this.lastFilledPeriodMs,
      'restarting',
      this.restarting,
    )
    // Same-period reloads (enter, scroll-back, the re-anchor below) already
    // refill through their own paths; only a genuine timeframe switch changes
    // the display period under us. `lastFilledPeriodMs` is set synchronously in
    // `doFill` before its first await, so the re-anchor's own `data:loaded`
    // observes the new period here and short-circuits — the loop guard.
    if (displayPeriod.ms === this.lastFilledPeriodMs) return
    void this.onTimeframeSwitch(displayPeriod)
  }

  /**
   * Re-anchor + fast-forward for a mid-replay timeframe switch (spec Addendum
   * §"Timeframe switch mid-replay"). The chart re-anchors at the last COMPLETED
   * display bucket on the NEW period, refills the sub-buffer from that bucket's
   * start, then fast-forwards — merging sub-bars WITHOUT pacing — up to the
   * exact instant the user had reached. The switch lands on the same moment
   * with a partially-formed candle: no visual rewind, no lost progress.
   */
  private async onTimeframeSwitch(displayPeriod: Period): Promise<void> {
    // Snapshot the reached instant BEFORE the fast-forward rewrites it.
    const reached = this.lastAppliedTs
    replayDebug('tf-switch, reached', iso(reached), 'looping', this.state.looping)
    this.forwardBuffer = []
    this.lastBufferedTs = null
    this.reachedLiveEdge = false
    this.state.exhausted = false
    // New display period → fresh ladder; a coarse escalation from the old
    // period must not stick (its rung may be coarser than the new display).
    this.subEscalation = null
    this.stagedAnyAtRung = false
    this.forming = null
    this.formingBucketTs = null

    // The current (partial) display bucket on the new period. Re-anchor one tick
    // before its start so every completed bucket loads and this one re-forms
    // from its sub-bars.
    const bucketStart =
      reached != null
        ? alignTimestamp(reached, displayPeriod, this.deps.regularSession?.())
        : this.startTs != null
          ? alignTimestamp(this.startTs, displayPeriod, this.deps.regularSession?.())
          : null
    const anchorTarget = bucketStart != null ? bucketStart - 1 : null
    if (anchorTarget != null && this.lastAnchorTs !== anchorTarget) {
      this.lastAnchorTs = anchorTarget
      void Promise.resolve(this.deps.applyAnchor(anchorTarget)).catch(() => undefined)
    }

    // Refill sub-bars from the partial bucket's start (not the reached instant),
    // so the fast-forward re-forms the whole partial candle. `fillFloorTs` drops
    // to the bucket start so those sub-bars aren't filtered as already-seen.
    this.scanFromTs = bucketStart ?? this.startTs ?? null
    this.fillFloorTs = (bucketStart ?? this.startTs ?? 0) - 1

    await this.fillForward().catch(() => undefined)
    if (!this.state.active) return

    // Fast-forward: apply the partial bucket's sub-bars up to `reached` with no
    // pacing (the gap-walk keeps refilling if the bucket spans a fill boundary).
    // Nothing stepped yet → nothing to fast-forward; the first step reveals it.
    if (reached != null) {
      let guard = 0
      while (guard++ < FAST_FORWARD_CAP) {
        while (this.forwardBuffer.length === 0 && !this.reachedLiveEdge) {
          const scanBefore = this.scanFromTs
          await this.fillForward().catch(() => undefined)
          if (!this.state.active) return
          if (this.forwardBuffer.length === 0 && this.scanFromTs === scanBefore) break
        }
        const next = this.forwardBuffer[0]
        if (!next || next.timestamp > reached) break
        this.applyOneSubBar(this.forwardBuffer.shift()!)
      }
    }
  }

  /** In-flight-guarded forward fill; concurrent callers share one fetch cycle. */
  private fillForward(): Promise<void> {
    if (this.fillPromise) return this.fillPromise
    this.fillPromise = this.doFill().finally(() => {
      this.fillPromise = null
    })
    return this.fillPromise
  }

  /**
   * The scan walked start→now at the current sub-resolution and staged NOTHING
   * — the finer series simply doesn't cover this range (shorter retention).
   * Escalate one rung (1m → 5m → 30m → 1D → display) and rescan from the
   * un-replayed edge so replay degrades toward whole-bar mode instead of
   * declaring a bogus "caught up". Returns true when a coarser rung was armed;
   * the caller breaks and the next fill (step's gap-walk keeps the loop going)
   * scans at the new rung.
   */
  private tryEscalateRung(currentSub: Period, displayPeriod: Period): boolean {
    if (this.stagedAnyAtRung) return false
    const next = nextSubRung(currentSub, displayPeriod)
    if (!next) return false
    this.subEscalation = next
    this.stagedAnyAtRung = false
    this.lastBufferedTs = null
    this.scanFromTs =
      this.lastAppliedTs != null ? this.lastAppliedTs + next.ms : (this.startTs ?? 0)
    replayDebug(
      'escalate rung',
      currentSub.label,
      '→',
      next.label,
      'rescan from',
      iso(this.scanFromTs),
    )
    return true
  }

  private async doFill(): Promise<void> {
    if (!this.state.active || this.reachedLiveEdge) return
    // A loop wrap resets the buffers and bumps the epoch mid-flight; a fill
    // that awoke under a stale epoch must discard its result, not admit bars
    // fetched for a scan position that no longer exists.
    const epoch = this.fillEpoch
    // Fetch at the LADDER's sub-resolution — scan/probe/live-edge math is all in
    // sub-period units — but track the DISPLAY period for TF-switch detection.
    const displayPeriod = this.deps.periodOf()
    const subPeriod = this.subEscalation ?? subPeriodFor(displayPeriod)
    const subMs = subPeriod.ms
    const pageMs = pageMsFromBars(subPeriod)
    this.lastFilledPeriodMs = displayPeriod.ms
    const now = this.deps.nowMs()

    // Resume from the persisted scan position. Each probe advances it, so a
    // gap wider than one fill's probe budget (weeks of missing intraday data,
    // long holiday runs) is crossed by SUCCESSIVE fills — never rescanned.
    let from =
      this.scanFromTs ??
      (this.lastBufferedTs != null ? this.lastBufferedTs + subMs : (this.startTs ?? 0))
    let probes = 0
    while (probes < MAX_PROBES) {
      if (from > now) {
        if (this.tryEscalateRung(subPeriod, displayPeriod)) break
        replayDebug('live edge (scan past now)', iso(from))
        this.reachedLiveEdge = true
        break
      }
      const to = Math.min(from + pageMs, now)
      const bars = await this.deps.fetchBars(subPeriod, from, to)
      replayDebug(
        'fill probe',
        subPeriod.label,
        iso(from),
        '→',
        iso(to),
        'got',
        bars.length,
        'bars',
      )
      // A view change (exit / new symbol) or a loop wrap may have landed
      // mid-fetch.
      if (!this.state.active || epoch !== this.fillEpoch) return
      // Admit any sub-bar past the staged high-water (or, before the first fill,
      // past `fillFloorTs` — set below the first sub-bar to reveal on enter and
      // to the partial-bucket start on a TF switch, so those sub-bars pass).
      const cutoff = this.lastBufferedTs ?? this.fillFloorTs
      const fresh = bars
        .filter((b) => b.timestamp > cutoff)
        .sort((a, b) => a.timestamp - b.timestamp)
      replayDebug(
        '  admitted',
        fresh.length,
        'cutoff',
        iso(this.lastBufferedTs ?? this.fillFloorTs),
      )
      if (fresh.length > 0) {
        this.stagedAnyAtRung = true
        this.forwardBuffer.push(...fresh)
        this.lastBufferedTs = fresh[fresh.length - 1].timestamp
        this.scanFromTs = this.lastBufferedTs + subMs
        if (this.forwardBuffer.length >= LOW_WATER) break
        from = this.scanFromTs
      } else {
        // Empty window — a weekend/holiday/data gap. Walk forward past it
        // (bounded per fill, persistent across fills).
        if (to >= now) {
          if (this.tryEscalateRung(subPeriod, displayPeriod)) break
          replayDebug('live edge (empty window to now)')
          this.reachedLiveEdge = true
          break
        }
        from = to + subMs
        this.scanFromTs = from
      }
      probes += 1
    }
  }
}
